import * as React from "react";
import { useRef } from "react";
import { WorkflowNode, Question, questionDefs } from "../../types";
import { Divider, FormGroup, ToggleGroup } from "../RightAsideHelpers";
import Input from "@/components/Input";
import IconPlus from "@/icons/IconPlus";
import Select from "@/components/Select";
import { useMemo } from "react";
import IconClose from "@/icons/IconClose";
import TextArea from "@/components/TextArea";
import VariablePicker from "@/components/VariablePicker";
import VariableHelpCard from "@/components/VariableHelpCard";
import FieldNamePicker from "@/components/FieldNamePicker";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function AskQuestionEditor({ node, onUpdate }: Props) {
  const options = node.options || [];
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const questionOptions = useMemo(() => {
    const result: Record<string, string> = {};
    for (const key of Object.keys(questionDefs) as Question[]) {
      result[key] = questionDefs[key].label;
    }
    return result;
  }, []);

  function addOption() {
    //if this is the first option, check to see if this node had a child,
    //it needs to become the child of the first option so that it doesn't get lost.
    const optionChild = options.length === 0 ? node.child : undefined;

    onUpdate({
      options: [...options, { label: "", childNodeId: optionChild }]
    });
  }

  function removeOption(index: number) {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    onUpdate({ options: newOptions });
  }

  function toggleInvalidReplyAction(
    invalidReplyAction: "repeat" | "failBranch"
  ) {
    onUpdate({ invalidReplyAction });
  }

  function handleInsertIntoQuestion(variable: string) {
    const el = textAreaRef.current;
    if (el) {
      const start = el.selectionStart ?? (node.question?.length || 0);
      const end = el.selectionEnd ?? start;
      const current = node.question || "";
      const newText =
        current.substring(0, start) + variable + current.substring(end);
      onUpdate({ question: newText });
      requestAnimationFrame(() => {
        el.focus();
        const newPos = start + variable.length;
        el.setSelectionRange(newPos, newPos);
      });
    } else {
      onUpdate({ question: (node.question || "") + variable });
    }
  }

  return (
    <>
      <VariableHelpCard context="askQuestion" />
      <FormGroup label="Question Text">
        <div className="relative">
          <div className="absolute right-1 top-1 z-10">
            <VariablePicker onInsert={handleInsertIntoQuestion} />
          </div>
          <TextArea
            ref={textAreaRef}
            value={node.question || ""}
            onChange={(e) => onUpdate({ question: e.currentTarget.value })}
          />
        </div>
      </FormGroup>
      <FormGroup label="Question Type">
        <Select
          options={questionOptions}
          value={node.questionType}
          onChange={(type) => {
            // When switching to multipleChoice, initialize with one empty option
            if (
              type === "multipleChoice" &&
              (!node.options || node.options.length === 0)
            ) {
              const optionChild = node.child; // preserve existing child if any
              onUpdate({
                questionType: type,
                options: [{ label: "", childNodeId: optionChild }]
              });
            } else {
              onUpdate({ questionType: type });
            }
          }}
        />
        {node.questionType === "multipleChoice" && (
          <div className="flex flex-col gap-2 mt-2">
            {options.map((option, i) => (
              <div key={i}>
                <Input
                  suffixIcon={
                    <div
                      className="cursor-pointer"
                      onClick={() => removeOption(i)}
                    >
                      <IconClose />
                    </div>
                  }
                  value={option.label || ""}
                  onChange={(label) => {
                    const newOptions = [...options];
                    newOptions[i] = { ...option, label };
                    onUpdate({ options: newOptions });
                  }}
                  onBlur={(label) => {
                    const trimmedLabel = label.trim();
                    if (trimmedLabel !== label) {
                      const newOptions = [...options];
                      newOptions[i] = { ...option, label: trimmedLabel };
                      onUpdate({ options: newOptions });
                    }
                  }}
                />
              </div>
            ))}
            <div
              onClick={addOption}
              className="cursor-pointer w-full h-10 px-6 py-2.5 bg-white rounded-[8px] border-[1.5px] border-blue-500 justify-center items-center gap-2 inline-flex"
            >
              <div className="text-center text-blue-500 text-sm flex items-center font-bold gap-2">
                <IconPlus className="text-blue-500" />
                <span> Add Option</span>
              </div>
            </div>
          </div>
        )}
      </FormGroup>
      {node.questionType !== "multipleChoice" && (
        <FormGroup label="Character Limit">
          <Input
            type="number"
            value={node.characterLimit || ""}
            onChange={(characterLimit) =>
              onUpdate({ characterLimit: Number(characterLimit) })
            }
          />
        </FormGroup>
      )}
      <Divider />
      <FormGroup label="Save Reply As">
        <ToggleGroup
          toggled={node.saveAsContactProp || false}
          label="Contact Variable"
          helpText="Saved forever with this customer across all conversations. Use for: email, name, preferences, customer tier."
          changeToggle={() => {
            onUpdate({
              saveAsContactProp: !node.saveAsContactProp
            });
          }}
        />
        <ToggleGroup
          toggled={node.saveAsConversationProp || false}
          label="Conversation Variable"
          helpText="Saved for this conversation, shared across all workflows in this chat. Use for: ticket numbers, issue category, current request."
          changeToggle={() => {
            onUpdate({
              saveAsConversationProp: !node.saveAsConversationProp
            });
          }}
        />
        <ToggleGroup
          toggled={node.saveAsWorkflowProp || false}
          label="Workflow Variable"
          helpText="Temporary and isolated to this workflow run only. Use for: counters, calculations, integrations."
          changeToggle={() => {
            onUpdate({
              saveAsWorkflowProp: !node.saveAsWorkflowProp
            });
          }}
        />
        {(node.saveAsContactProp ||
          node.saveAsConversationProp ||
          node.saveAsWorkflowProp) && (() => {
            const activeScopes = [
              node.saveAsContactProp && "contact",
              node.saveAsConversationProp && "conversation",
              node.saveAsWorkflowProp && "workflow"
            ].filter(Boolean) as string[];
            return (
              <FieldNamePicker
                value={node.propName ?? ""}
                onChange={(propName) => onUpdate({ propName })}
                scopes={activeScopes}
                placeholder="Which variable to save answer into?"
              />
            );
          })()}
      </FormGroup>
      {node.questionType !== "multipleChoice" && (
        <>
          <Divider />
          <FormGroup label="Invalid Reply Action">
            <Select
              options={{
                repeat: "Repeat the question",
                failBranch: "Proceed to a fail branch"
              }}
              value={node.invalidReplyAction}
              onChange={toggleInvalidReplyAction}
            />
          </FormGroup>
        </>
      )}
    </>
  );
}
